<script setup lang="ts">
import { onMounted, watch } from 'vue';
import { useForm } from 'vee-validate';
import { yup, validators } from '@clearbanc/validation';
import statesByCountry from '@/data/states-by-country';
import { COUNTRY_CODES } from '@/data/supported-country-codes';
import { parsePlaceData, PlaceDataOutput } from '@/utils/address';
import {
  stateLabel,
  postalCodeLabel,
  stateOptionsForCountry,
} from '@/utils/local';
import {
  countryOptionsWithPriority,
  isCountryWithPostalCode,
} from '@/data/country-code-with-names';
import { BusinessDetails } from '@/types/business.types';
import { BusinessShippingAddress } from '@/types/address.types';

import InputTextGroup from '@/components/forms/groups/InputTextGroup.vue';
import DropdownGroup from '@/components/forms/groups/DropdownGroup.vue';
import AutoCompleteAddress from '@/components/AutoCompleteAddress.vue';
import CheckboxGroup from '@/components/forms/groups/CheckboxGroup.vue';

interface BusinessAddressProps extends BusinessShippingAddress {
  shippingAddressSameCheckbox: string[];
}

const props = defineProps<{ business: BusinessDetails }>();

// ---------
// Variables
// ---------

const countries = countryOptionsWithPriority([
  COUNTRY_CODES.US,
  COUNTRY_CODES.CA,
  COUNTRY_CODES.GB,
]);

const states = (country: string) => {
  return statesByCountry[country] || [];
};

// ----------
// Form Logic
// ----------

const validationSchema = yup.object({
  line1: yup.address(),
  country: yup.string(),
  city: yup.city(),
  // TODO SC-192429 - We can revert this after updating typescript definitions
  // in the @clearbanc/validation to include these missing methods
  state: (yup.string() as any).requiredWhen('country', (country: string) =>
    stateOptionsForCountry(country),
  ),
  postalCode: yup.postalCode(),
  line1Shipping: (yup.address() as any).requiredWhen(
    'shippingAddressSame',
    (shippingAddressSame: boolean) => !shippingAddressSame,
  ),
  countryShipping: (yup.string() as any).requiredWhen(
    'shippingAddressSame',
    (shippingAddressSame: boolean) => !shippingAddressSame,
  ),
  cityShipping: (yup.string().nullable() as any).requiredWhen(
    'shippingAddressSame',
    (shippingAddressSame: boolean) => !shippingAddressSame,
  ),
  stateShipping: (yup.string() as any).requiredWhen(
    ['shippingAddressSame', 'countryShipping'],
    (shippingAddressSame: boolean, countryShipping: any) =>
      !shippingAddressSame && stateOptionsForCountry(countryShipping),
  ),
  postalCodeShipping: (yup.string().nullable() as any).validator(
    'test-shipping-postal-code',
    ['shippingAddressSame', 'countryShipping'],
    (value: any, shippingAddressSame: boolean, countryShipping: any) => {
      return shippingAddressSame
        ? undefined
        : validators.validatePostalCode(value, countryShipping);
    },
  ),
});

const { values, setValues, validate } = useForm<BusinessAddressProps>({
  validationSchema,
});

async function validateAndGetValues() {
  const { valid } = await validate();
  if (valid) {
    const address = {
      line1: values.line1,
      line2: values.line2,
      country: values.country,
      state: values.state,
      city: values.city,
      postalCode: values.postalCode,
    };
    const shippingAddress = values.shippingAddressSame
      ? address
      : {
          line1: values.line1Shipping,
          line2: values.line2Shipping,
          country: values.countryShipping,
          state: values.stateShipping,
          city: values.cityShipping,
          postalCode: values.postalCodeShipping,
        };

    return {
      address,
      shippingAddress,
      shippingAddressSame: values.shippingAddressSame,
    };
  }
  return undefined;
}

defineExpose({
  validateAndGetValues,
});

function handleAddressSelected(placeData: PlaceDataOutput, shipping = false) {
  const suffix = shipping ? 'Shipping' : '';
  const parsedData = parsePlaceData(placeData);

  setValues({
    ...values,
    [`line1${suffix}`]: parsedData.line1,
    [`line2${suffix}`]: parsedData.line2,
    [`city${suffix}`]: parsedData.city,
    [`country${suffix}`]: parsedData.country?.value,
    [`state${suffix}`]: parsedData.state?.value,
    [`postalCode${suffix}`]: parsedData.postalCode,
  });
}

function populateSavedValues() {
  const {
    shippingAddressSame,
    addressStreet1,
    addressStreet2,
    addressCity,
    addressCountryCode,
    addressState,
    addressPostalCode,
    shippingAddressStreet1,
    shippingAddressStreet2,
    shippingAddressCity,
    shippingAddressCountryCode,
    shippingAddressState,
    shippingAddressPostalCode,
  } = props.business;

  setValues({
    shippingAddressSameCheckbox: shippingAddressSame ? ['same'] : [],
    shippingAddressSame,
    line1: addressStreet1,
    line2: addressStreet2,
    city: addressCity,
    country: addressCountryCode,
    state: addressState,
    postalCode: addressPostalCode,

    line1Shipping: shippingAddressStreet1,
    line2Shipping: shippingAddressStreet2,
    cityShipping: shippingAddressCity,
    countryShipping: shippingAddressCountryCode,
    stateShipping: shippingAddressState,
    postalCodeShipping: shippingAddressPostalCode,
  });
}

onMounted(() => {
  populateSavedValues();
});

watch(
  () => values.shippingAddressSameCheckbox,
  (newValue) => {
    setValues({
      ...values,
      shippingAddressSame: !!newValue?.includes('same'),
    });
  },
);
</script>

<template>
  <div class="c-group">
    <div class="c-group__header">
      <h3 class="c-group__header__title">Registered Address</h3>
    </div>

    <div class="c-form__row grid grid-cols-12">
      <div class="col-span-6 md:col-span-12">
        <AutoCompleteAddress
          name="line1"
          label="Company Address"
          @addressSelected="
            (place: PlaceDataOutput) => handleAddressSelected(place)
          "
        />
      </div>
      <div class="col-span-6 md:col-span-12">
        <InputTextGroup name="line2" label="Company Address Line 2" />
      </div>
    </div>

    <div class="c-form__row grid grid-cols-12">
      <div class="col-span-6 md:col-span-12">
        <DropdownGroup
          name="country"
          label="Country"
          :options="countries"
          option-label="nameString"
          placeholder="Select"
          append-to="self"
          option-value="value"
          :filter="true"
          :reset-filter-on-hide="true"
          @change="
            setValues({
              ...values,
              state: undefined,
              postalCode: undefined,
            })
          "
        />
      </div>
      <div class="col-span-6 md:col-span-12">
        <InputTextGroup name="city" label="City" />
      </div>
    </div>

    <div class="c-form__row grid grid-cols-12">
      <div
        v-if="stateOptionsForCountry(values.country)"
        class="col-span-6 md:col-span-12"
      >
        <DropdownGroup
          name="state"
          :label="stateLabel(values.country)"
          :options="states(values.country)"
          option-label="nameString"
          option-value="value"
          placeholder="Select"
          append-to="self"
          :filter="true"
          :reset-filter-on-hide="true"
        />
      </div>
      <div
        v-if="isCountryWithPostalCode(values.country)"
        class="col-span-6 md:col-span-12"
      >
        <InputTextGroup
          name="postalCode"
          :label="postalCodeLabel(values.country)"
        />
      </div>
    </div>
  </div>

  <div class="c-group">
    <div class="c-group__header">
      <h3 class="c-group__header__title">Mailing Address</h3>
    </div>

    <div class="c-form__row">
      <CheckboxGroup
        name="shippingAddressSameCheckbox"
        :options="[
          {
            value: 'same',
            label: 'Same as Registered Address',
          },
        ]"
      />
    </div>

    <template v-if="!values.shippingAddressSame" :style="{ display: 'block' }">
      <div class="c-form__row grid grid-cols-12">
        <div class="col-span-6 md:col-span-12">
          <AutoCompleteAddress
            name="line1Shipping"
            label="Company Address"
            @addressSelected="
              (place: any) => handleAddressSelected(place, true)
            "
          />
        </div>
        <div class="col-span-6 md:col-span-12">
          <InputTextGroup name="line2Shipping" label="Company Address Line 2" />
        </div>
      </div>

      <div class="c-form__row grid grid-cols-12">
        <div class="col-span-6 md:col-span-12">
          <DropdownGroup
            name="countryShipping"
            label="Country"
            :options="countries"
            option-label="nameString"
            option-value="value"
            placeholder="Select"
            append-to="self"
            :filter="true"
            :reset-filter-on-hide="true"
            @change="
              setValues({
                ...values,
                stateShipping: undefined,
                postalCodeShipping: undefined,
              })
            "
          />
        </div>
        <div class="col-span-6 md:col-span-12">
          <InputTextGroup name="cityShipping" label="City" />
        </div>
      </div>

      <div class="c-form__row grid grid-cols-12">
        <div
          v-if="stateOptionsForCountry(values.countryShipping)"
          class="col-span-6 md:col-span-12"
        >
          <DropdownGroup
            name="stateShipping"
            :label="stateLabel(values.countryShipping)"
            :options="states(values.countryShipping ?? '')"
            option-label="nameString"
            option-value="value"
            placeholder="Select"
            append-to="self"
            :filter="true"
            :reset-filter-on-hide="true"
          />
        </div>
        <div
          v-if="isCountryWithPostalCode(values.countryShipping)"
          class="col-span-6 md:col-span-12"
        >
          <InputTextGroup
            name="postalCodeShipping"
            :label="postalCodeLabel(values.countryShipping ?? '')"
          />
        </div>
      </div>
    </template>
  </div>
</template>
